// frontend/src/pages/TicketDetailPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supportApi } from '../api/support';
import { useAuth } from '../context/AuthContext';
import uploadService from '../services/upload';
import { compressImage } from '../utils/imageCompression';
import { FaArrowLeft, FaPaperPlane, FaPaperclip, FaSpinner, FaUser, FaUserShield } from 'react-icons/fa';

function TicketDetailPage() {
  const { ticketId } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const messagesEndRef = useRef(null);

  const fetchTicket = useCallback(async () => {
    try {
      const data = await supportApi.getTicketById(ticketId);
      setTicket(data);
    } catch (err) {
      console.error("Failed to load ticket", err);
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [ticket?.messages]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const compressed = await compressImage(file, 0.6);
        const res = await uploadService.uploadImage(compressed, 'support-attachments');
        return res.url;
      });
      const urls = await Promise.all(uploadPromises);
      setAttachments(prev => [...prev, ...urls]);
    } catch (err) {
      alert("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      await supportApi.replyToTicket(ticketId, {
        message: replyText,
        attachments: attachments,
        is_internal_note: false
      });
      setReplyText('');
      setAttachments([]);
      fetchTicket(); 
    } catch (err) {
      alert("Failed to send reply.");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><FaSpinner className="animate-spin text-4xl text-primary" /></div>;
  if (!ticket) return <div className="text-center py-20">Ticket not found.</div>;

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        
        <Link to="/account?tab=support" className="text-gray-500 hover:text-primary flex items-center gap-2 mb-6">
          <FaArrowLeft /> Back to Dashboard
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* LEFT: Ticket Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <div className="mb-4">
                <span className="text-xs font-bold text-gray-400 uppercase">Ticket ID</span>
                <h2 className="text-xl font-bold text-gray-800">{ticket.ticket_id}</h2>
              </div>
              
              <div className="mb-4">
                <span className="text-xs font-bold text-gray-400 uppercase">Status</span>
                <div className={`mt-1 inline-block px-3 py-1 rounded-full text-xs font-bold uppercase 
                  ${ticket.status === 'open' ? 'bg-green-100 text-green-800' : 
                    ticket.status === 'closed' ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-800'}`}>
                  {ticket.status ? ticket.status.replace('_', ' ') : 'Unknown'}
                </div>
              </div>

              <div className="mb-4">
                <span className="text-xs font-bold text-gray-400 uppercase">Category</span>
                <p className="text-gray-700 font-medium">{ticket.category}</p>
              </div>

              {ticket.related_order_id && (
                <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-100">
                  <span className="text-xs font-bold text-blue-800 uppercase">Related Order</span>
                  <p className="text-sm text-blue-600 font-mono mt-1">ID: {ticket.related_order_id}</p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Conversation */}
          <div className="lg:col-span-2 flex flex-col h-[80vh]">
            <div className="bg-white rounded-t-lg shadow-sm p-4 border-b border-gray-200">
              <h1 className="text-lg font-bold text-gray-800">{ticket.subject}</h1>
            </div>

            {/* Messages Area */}
            <div className="flex-grow overflow-y-auto bg-gray-100 p-4 space-y-4">
              {ticket.messages && ticket.messages.map((msg) => {
                if (msg.is_internal_note && !user.roles.includes('admin')) return null;

                const isMe = msg.sender_role === 'customer';
                
                return (
                  <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-lg p-4 shadow-sm ${isMe ? 'bg-blue-600 text-white' : 'bg-white text-gray-800'}`}>
                      <div className="flex items-center gap-2 mb-2 text-xs opacity-75">
                        {isMe ? <FaUser /> : <FaUserShield />}
                        <span className="font-bold">{msg.sender_name}</span>
                        <span>• {new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      
                      <p className="whitespace-pre-wrap text-sm">{msg.message}</p>

                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {msg.attachments.map((url, idx) => (
                            <a key={idx} href={url} target="_blank" rel="noopener noreferrer">
                              <img src={url} alt="attachment" className="w-20 h-20 object-cover rounded border border-white/20" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Box */}
            <div className="bg-white rounded-b-lg shadow-sm p-4 border-t border-gray-200">
              {ticket.status === 'closed' ? (
                <div className="text-center text-gray-500 py-4 bg-gray-50 rounded">
                  This ticket is closed. You cannot reply.
                </div>
              ) : (
                <form onSubmit={handleReply}>
                  {attachments.length > 0 && (
                    <div className="flex gap-2 mb-2 overflow-x-auto">
                      {attachments.map((url, idx) => (
                        <img key={idx} src={url} alt="preview" className="w-12 h-12 object-cover rounded border" />
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <label className="cursor-pointer p-3 text-gray-500 hover:bg-gray-100 rounded transition">
                      <FaPaperclip />
                      <input type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                    </label>
                    
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-grow p-2 border border-gray-300 rounded focus:outline-none focus:border-primary resize-none"
                      rows="2"
                      disabled={sending}
                    ></textarea>
                    
                    <button 
                      type="submit" 
                      disabled={sending || uploading || (!replyText.trim() && attachments.length === 0)}
                      className="bg-primary hover:bg-primary-dark text-white px-6 rounded font-bold transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sending ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default TicketDetailPage;